import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ArtTile } from '../components/ArtTile';
import { ArtModal } from '../components/ArtModal';
import { theme } from '../theme';
import { itemHasTag, normalizeTags } from '../utils/tags';

const TagFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.space[2]}px;
  margin-bottom: ${theme.space[6]}px;
`;

const TagToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]}px;
  margin-bottom: ${theme.space[4]}px;
`;

const ToggleBtn = styled.button`
  appearance: none;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.muted};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.sm};
  padding: 6px 14px;
  border-radius: ${theme.radii.pill}px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${theme.colors.accent};
    color: ${theme.colors.accent};
  }
`;

const FilterBtn = styled.button`
  appearance: none;
  border: 1px solid ${({ $active }) => ($active ? theme.colors.accent : theme.colors.border)};
  background: ${({ $active }) => ($active ? theme.colors.accentSoft : theme.colors.surface)};
  color: ${({ $active }) => ($active ? theme.colors.accent : theme.colors.muted)};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.sm};
  padding: 6px 14px;
  border-radius: ${theme.radii.pill}px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${theme.colors.accent};
    color: ${theme.colors.accent};
  }
`;

const YearSection = styled.section`
  background: ${theme.colors.gallery};
  overflow: hidden;

  &:not(:first-child) {
    margin-top: ${theme.space[5]}px;
  }
`;

const YearHeading = styled.h2`
  background: ${theme.colors.accent};
  color: #fff;
  font-family: ${theme.fonts.display};
  font-size: ${theme.fontSizes.lg};
  font-weight: normal;
  letter-spacing: 0.04em;
  text-align: center;
  margin: 0;
  padding: ${theme.space[3]}px ${theme.space[4]}px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 2px;
  gap: 0;
  align-items: start;
  background: ${theme.colors.gallery};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  /* keep 2 columns on mobile */
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Status = styled.p`
  color: ${({ $error }) => ($error ? theme.colors.accent : theme.colors.muted)};
  font-size: ${theme.fontSizes.sm};
`;

function groupByYear(items) {
  const groups = new Map();

  items.forEach((item) => {
    const year = new Date(item.date).getFullYear();
    if (!groups.has(year)) groups.set(year, []);
    groups.get(year).push(item);
  });

  return [...groups.entries()]
    .sort(([a], [b]) => b - a)
    .map(([year, yearItems]) => ({
      year,
      items: [...yearItems].sort((a, b) => Number(b.id) - Number(a.id)),
    }));
}

function YearGrid({ items, onSelect }) {
  return (
    <Grid>
      {items.map((item) => (
        <ArtTile
          key={item.id}
          title={item.title}
          imageUrl={item.imageUrl}
          onClick={() => onSelect(item)}
        />
      ))}
    </Grid>
  );
}

export function Gallery() {
  const [art, setArt] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activeTag, setActiveTag] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/art/art.json`)
      .then((res) => {
        if (!res.ok) throw new Error('Could not load gallery');
        return res.json();
      })
      .then((items) => {
        const sorted = [...items].sort(
          (a, b) => Number(b.id) - Number(a.id)
        );
        setArt(sorted);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const allTags = useMemo(() => {
    const tags = new Set();
    art.forEach((item) => normalizeTags(item.tags).forEach((tag) => tags.add(tag)));
    return [...tags].sort((a, b) => a.localeCompare(b));
  }, [art]);

  const filteredArt = useMemo(() => {
    if (activeTag === 'all') return art;
    return art.filter((item) => itemHasTag(item, activeTag));
  }, [art, activeTag]);

  const sections = useMemo(() => groupByYear(filteredArt), [filteredArt]);

  if (loading) return <Status>Loading gallery…</Status>;
  if (error) {
    return <Status $error>{error}</Status>;
  }

  return (
    <>
      {art.length === 0 ? (
        <Status>No pieces yet — add images to <code>public/art/images/</code> and entries to <code>public/art/art.json</code>.</Status>
      ) : (
        <>
          {allTags.length > 0 && (
            <>
              <TagToolbar>
                <ToggleBtn
                  type="button"
                  onClick={() => setShowFilters((v) => !v)}
                  aria-expanded={showFilters}
                >
                  {showFilters ? 'Hide Tags' : 'Show Tags'}
                </ToggleBtn>
                {activeTag !== 'all' && (
                  <FilterBtn type="button" $active onClick={() => setActiveTag('all')}>
                    {activeTag} ×
                  </FilterBtn>
                )}
              </TagToolbar>
              {showFilters && (
                <TagFilter>
                  <FilterBtn
                    type="button"
                    $active={activeTag === 'all'}
                    onClick={() => setActiveTag('all')}
                  >
                    All
                  </FilterBtn>
                  {allTags.map((tag) => (
                    <FilterBtn
                      key={tag}
                      type="button"
                      $active={activeTag === tag}
                      onClick={() => setActiveTag(tag)}
                    >
                      {tag}
                    </FilterBtn>
                  ))}
                </TagFilter>
              )}
            </>
          )}

          {filteredArt.length === 0 ? (
            <Status>No pieces with that tag.</Status>
          ) : (
            sections.map(({ year, items }) => (
              <YearSection key={year}>
                <YearHeading>{year}</YearHeading>
                <YearGrid items={items} onSelect={setSelected} />
              </YearSection>
            ))
          )}
        </>
      )}

      {selected && (
        <ArtModal piece={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}
